import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { getMarketData } from '../api';
import {
  LayoutDashboard,
  Building2,
  Wallet,
  Receipt,
  DollarSign,
  BarChart3,
  LogOut,
  Menu,
  X,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [marketData, setMarketData] = useState(null);

  useEffect(() => {
    fetchMarketData();
    const interval = setInterval(fetchMarketData, 900000); // 15 minutes
    return () => clearInterval(interval);
  }, []);

  const fetchMarketData = async () => {
    try {
      const response = await getMarketData();
      setMarketData(response.data);
    } catch (error) {
      console.error('Failed to fetch market data');
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Companies', href: '/companies', icon: Building2 },
    { name: 'Bank Accounts', href: '/banks', icon: Wallet },
    { name: 'Expected Receipts', href: '/expected-receipts', icon: Receipt },
    { name: 'Payments', href: '/payments', icon: DollarSign },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 }
  ];

  const MarketTicker = () => {
    if (!marketData) return null;

    const items = [
      { label: 'USD/INR', value: marketData.usd_inr, currency: true },
      { label: 'Gold', value: marketData.gold_price, prefix: '₹', suffix: '/10g' },
      { label: 'Crude Oil', value: marketData.crude_oil, prefix: '₹', suffix: '/bbl' },
      ...(marketData.sp500 ? [{
        label: 'S&P 500',
        value: marketData.sp500.value,
        change: marketData.sp500.changePercent
      }] : []),
      ...(marketData.nasdaq ? [{
        label: 'NASDAQ',
        value: marketData.nasdaq.value,
        change: marketData.nasdaq.changePercent
      }] : []),
      ...(marketData.nifty50 ? [{
        label: 'NIFTY 50',
        value: marketData.nifty50.value,
        change: marketData.nifty50.changePercent
      }] : []),
      ...(marketData.sensex ? [{
        label: 'SENSEX',
        value: marketData.sensex.value,
        change: marketData.sensex.changePercent
      }] : [])
    ];

    return (
      <div className="bg-slate-900 text-white py-2 overflow-hidden" data-testid="market-ticker">
        <div className="flex gap-8 ticker-animation" style={{ width: '200%' }}>
          {[...items, ...items].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-slate-400 text-sm">{item.label}:</span>
              <span className="font-semibold text-sm">
                {item.currency && '₹'}
                {item.prefix}
                {item.value?.toFixed(2) || 'N/A'}
                {item.suffix}
              </span>
              {item.change !== undefined && (
                <span className={`flex items-center gap-1 text-xs ${
                  item.change >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {item.change >= 0 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {Math.abs(item.change).toFixed(2)}%
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Market Ticker */}
      <MarketTicker />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
                data-testid="mobile-menu-button"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-xl font-bold gradient-text" style={{ fontFamily: 'Space Grotesk' }}>
                Fund Flow Manager
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900" data-testid="user-name">{user?.full_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-[113px] left-0 z-30 h-[calc(100vh-113px)]
          w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg
                    transition-colors
                    ${isActive
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-slate-700 hover:bg-slate-100'
                    }
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;